import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from '@/lib/swal';
import { useStepComments, useAddComment } from '../../hooks/useStepComments';

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return fallback;
}

function fmtDateTime(v: string): string {
  return new Date(v).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

interface Props {
  inspectionId: string;
  stepExecutionId: string;
  commentsAllowed: boolean;
  canPost: boolean;
  isExpanded: boolean;
}

export function StepCommentsSection({
  inspectionId,
  stepExecutionId,
  commentsAllowed,
  canPost,
  isExpanded,
}: Props) {
  const [text, setText] = useState('');
  const { data: comments = [] } = useStepComments(inspectionId, stepExecutionId, isExpanded);
  const addMutation = useAddComment(inspectionId, stepExecutionId);

  if (!commentsAllowed && comments.length === 0) return null;

  async function handlePost() {
    if (!text.trim()) return;
    try {
      await addMutation.mutateAsync(text.trim());
      setText('');
      toast.success('Comment posted');
    } catch (e) {
      toast.error(extractMsg(e as Error, 'Failed to post comment'));
    }
  }

  return (
    <div className="space-y-3 border-t border-border pt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Comments {comments.length > 0 && `(${comments.length})`}
      </h4>

      {comments.length > 0 && (
        <div className="space-y-2">
          {comments.map((c) => {
            const author = c.authorName ?? c.postedBy?.fullName ?? 'Unknown';
            return (
              <div key={c.id} className="rounded-lg bg-muted/40 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-foreground">{author}</span>
                  <span className="text-xs text-muted-foreground">{fmtDateTime(c.createdAt)}</span>
                </div>
                <p className="mt-1 text-sm text-foreground whitespace-pre-wrap">{c.comment}</p>
              </div>
            );
          })}
        </div>
      )}

      {commentsAllowed && canPost && (
        <div className="flex gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Add a comment…"
            rows={2}
            maxLength={2000}
            disabled={addMutation.isPending}
            className="flex-1 resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => void handlePost()}
            disabled={!text.trim() || addMutation.isPending}
            className="flex h-[44px] min-w-[60px] shrink-0 items-center justify-center rounded-lg bg-primary px-3 text-sm font-medium text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50"
          >
            {addMutation.isPending ? (
              <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
            ) : (
              'Post'
            )}
          </button>
        </div>
      )}
    </div>
  );
}
